import React from 'react';
import { motion } from 'framer-motion';
import { User, Users } from 'lucide-react';

export const GenderSelector = ({ onSelect }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.3 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <div
      className="h-full w-full flex flex-col justify-center items-center px-8 md:px-12 lg:px-20 py-12"
      data-testid="gender-selector"
    >
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-4xl w-full"
      >
        <motion.div variants={itemVariants} className="text-center mb-16">
          <p className="font-sans text-lg md:text-xl text-[#D31113] uppercase tracking-[0.3em] mb-4 font-semibold">
            Before We Begin
          </p>
          <h2 className="pres-font-heading text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white mb-6">
            WHO ARE YOU PRESENTING TO?
          </h2>
          <p className="font-sans text-xl text-gray-400 max-w-2xl mx-auto">
            Select your audience to customize the visuals throughout the presentation.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
          {/* Male Athletes */}
          <motion.button
            variants={itemVariants}
            onClick={() => onSelect('male')}
            className="group relative bg-[#2A1D45]/50 backdrop-blur-md border-2 border-white/10 rounded-2xl p-10 text-center hover:border-[#3B82F6] transition-all duration-300 focus:outline-none"
            data-testid="select-male"
            whileHover={{ scale: 1.02, y: -5 }}
            whileTap={{ scale: 0.98 }}
          >
            {/* Glow effect on hover */}
            <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 bg-[#3B82F6]" />
            
            {/* Icon */}
            <div className="w-20 h-20 rounded-2xl bg-[#3B82F6]/20 flex items-center justify-center mb-6 mx-auto group-hover:bg-[#3B82F6]/40 transition-colors">
              <User className="w-10 h-10 text-[#3B82F6]" />
            </div>

            <h3 className="pres-font-heading text-3xl md:text-4xl font-bold text-[#3B82F6] uppercase mb-2">
              Male Athletes
            </h3>
            <p className="font-sans text-lg text-gray-400">
              Images & videos featuring male athletes
            </p>

            {/* Border on hover */}
            <div className="absolute inset-0 rounded-2xl border-2 border-[#3B82F6] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
          </motion.button>

          {/* Female Athletes */}
          <motion.button
            variants={itemVariants}
            onClick={() => onSelect('female')}
            className="group relative bg-[#2A1D45]/50 backdrop-blur-md border-2 border-white/10 rounded-2xl p-10 text-center hover:border-[#EC4899] transition-all duration-300 focus:outline-none"
            data-testid="select-female"
            whileHover={{ scale: 1.02, y: -5 }}
            whileTap={{ scale: 0.98 }}
          >
            {/* Glow effect on hover */}
            <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 bg-[#EC4899]" />
            
            {/* Icon */}
            <div className="w-20 h-20 rounded-2xl bg-[#EC4899]/20 flex items-center justify-center mb-6 mx-auto group-hover:bg-[#EC4899]/40 transition-colors">
              <Users className="w-10 h-10 text-[#EC4899]" />
            </div>

            <h3 className="pres-font-heading text-3xl md:text-4xl font-bold text-[#EC4899] uppercase mb-2">
              Female Athletes
            </h3>
            <p className="font-sans text-lg text-gray-400">
              Images & videos featuring female athletes
            </p>

            {/* Border on hover */}
            <div className="absolute inset-0 rounded-2xl border-2 border-[#EC4899] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
          </motion.button>
        </div>

        <motion.p 
          variants={itemVariants}
          className="text-center mt-12 text-gray-500 font-sans"
        >
          Press <kbd className="px-2 py-1 bg-white/5 rounded border border-white/10 mx-1">M</kbd> for Male or
          <kbd className="px-2 py-1 bg-white/5 rounded border border-white/10 mx-1">F</kbd> for Female
        </motion.p>
      </motion.div>
    </div>
  );
};
